import React, { FC, useEffect } from 'react';
import { Typography, Box, Stack, Portal } from '@mui/material';
import { StyledLink } from '../Components';
import { formatDateOnly } from '../Formatters';
import { selectRevisionFilter, setFilterModel, setSortModel } from './filterSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { GET_REVISIONS } from './revisions.graphql';
import { revisionTypeToDisplayText } from './RevisionModel';
import { useAuth } from 'oidc-react';
import { StyledPaper } from '../theme';
import { getShortName } from '../utils/string';
import { nbNO } from '@mui/x-data-grid-pro/locales';
import {
  DataGridPro,
  getGridSingleSelectOperators,
  GridColDef,
  GridFilterOperator,
  GridRenderCellParams,
  GridToolbarQuickFilter,
  GridValueGetter,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Revision } from './types';
import { ExcelExport } from './ExcelExport';
import { Check } from '@mui/icons-material';

export const Overview: FC = () => {
  const { data, error } = useQuery(GET_REVISIONS, { fetchPolicy: 'cache-and-network' });
  return (
    <StyledPaper>
      <Typography variant="h5" component="h3">
        Revisjoner
      </Typography>
      {error && <p>Error: {error.message}</p>}
      <SearchableRevisionTable revisions={data?.revisions?.filter((x) => !!x) ?? []} loading={!error && !data?.revisions} />
    </StyledPaper>
  );
};

const SearchableRevisionTable: FC<{ revisions: Revision[]; loading: boolean }> = ({ revisions, loading }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const apiRef = useGridApiRef();
  const auth = useAuth();

  const searchTerm = searchParams.get('searchTerm');
  const { filterModel, sortModel } = useSelector(selectRevisionFilter);

  useEffect(() => {
    if (searchTerm) {
      dispatch(
        setFilterModel({
          items: [],
          quickFilterValues: [searchTerm],
        }),
      );
    }
  }, [searchTerm, apiRef]);

  const categories = toDistictCategories(revisions);
  const subjectId = auth.userData?.profile.sub;
  const myCategories: any[] = toDistictCategories(revisions.filter((x) => x!.guide?.mainCategory?.ownerId === subjectId));

  const operator: GridFilterOperator<any, string> = {
    label: 'er mine',
    value: 'mine',
    getApplyFilterFn: (_filterItem, _column) => {
      return (value, _row, _column, _apiRef) => {
        return myCategories?.includes(value) ?? false;
      };
    },
  };

  const milstones: GridColDef<Revision>[] = [0, 1, 2, 3, 4, 5].map((index) => ({
    field: `milestone${index + 1}`,
    headerName: `Milepæl ${index + 1}`,
    type: 'boolean',
    width: 100,
    valueGetter: (_: GridValueGetter<Revision>, row) => isMilestoneCompleted(row, index),
    renderCell: (params: GridRenderCellParams<Revision, boolean>) => (params.value ? <Check fontSize="small" /> : null),
  }));

  const columns: GridColDef<Revision>[] = [
    {
      field: 'guide.docName',
      headerName: 'Nummer',
      valueGetter: (_: GridValueGetter<Revision>, row) => row.guide?.docName,
      renderCell: (params: GridRenderCellParams<Revision, string>) => (
        <StyledLink to={`/revision/${params.row.id}`}>
          {params.row.guide?.docName ?? <span style={{ fontStyle: 'italic' }}>Uten navn</span>}
        </StyledLink>
      ),
    },
    {
      field: 'guide.docTitle',
      headerName: 'Tittel',
      flex: 2,
      valueGetter: (_: GridValueGetter<Revision>, row) => row.guide?.docTitle || 'Uten tittel',
    },
    {
      field: 'guide.category.title',
      width: 200,
      headerName: 'Fagområde',
      type: 'singleSelect',
      valueOptions: categories.map((title) => ({
        value: title!,
        label: title!,
      })),
      filterOperators: [...getGridSingleSelectOperators(), operator],
      valueGetter: (_: GridValueGetter<Revision>, row) => row.guide?.mainCategory?.title,
    },
    {
      field: 'type',
      headerName: 'Revisjonstype',
      type: 'singleSelect',
      valueOptions: revisions
        .map((r) => r.type)
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .map((type) => ({
          value: type!,
          label: revisionTypeToDisplayText(type!),
        })),
      width: 150,
      valueFormatter: (value) => revisionTypeToDisplayText(value),
    },
    {
      field: 'projectLeads',
      headerName: 'Prosjektleder',
      width: 150,
      valueGetter: (_: GridValueGetter<Revision>, row) => getProjectLeadNames(row),
    },
    {
      field: 'createdAt',
      headerName: 'Opprettet',
      type: 'date',
      width: 120,
      valueGetter: (_: GridValueGetter<Revision>, row) => new Date(row.createdAt),
      valueFormatter: (value) => formatDateOnly(value),
    },
    ...milstones,
    {
      field: 'publishedVersion',
      headerName: 'Versjon',
      width: 100,
      valueGetter: (_: GridValueGetter<Revision>, row) => row.publishedVersion,
    },
    {
      field: 'publishedAt',
      headerName: 'Publisert',
      type: 'date',
      width: 120,
      valueGetter: (_: GridValueGetter<Revision>, row) => (row.publishedAt ? new Date(row.publishedAt) : null),
      valueFormatter: (value) => formatDateOnly(value),
    },
    {
      field: 'completedAt',
      headerName: 'Ferdig',
      type: 'date',
      width: 120,
      valueGetter: (_: GridValueGetter<Revision>, row) => (row.completedAt ? new Date(row.completedAt) : null),
      valueFormatter: (value) => formatDateOnly(value),
    },
  ];

  const inHistoryMode = searchTerm !== null;
  return (
    <Stack spacing={2} marginTop={2}>
      <ExcelExport apiRef={apiRef} />
      <Box id="filter-panel" />
      <DataGridPro
        rows={revisions}
        loading={loading}
        apiRef={apiRef}
        filterModel={filterModel}
        sortModel={sortModel}
        disableRowSelectionOnClick
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        pagination
        onFilterModelChange={(model) => dispatch(setFilterModel(model))}
        onSortModelChange={(model) => dispatch(setSortModel(model))}
        slots={{
          toolbar: CustomToolbar,
        }}
        density="compact"
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 50,
            },
          },
          columns: {
            columnVisibilityModel: {
              publishedAt: inHistoryMode,
              publishedVersion: inHistoryMode,
              milestone1: !inHistoryMode,
              milestone2: !inHistoryMode,
              milestone3: !inHistoryMode,
              milestone4: !inHistoryMode,
              milestone5: !inHistoryMode,
              milestone6: !inHistoryMode,
            },
          },
        }}
      />
    </Stack>
  );
};

function isMilestoneCompleted(revision: Revision, milestoneIndex: number) {
  return revision.checklist?.milestones?.[milestoneIndex]?.completedTasks === revision.checklist?.milestones?.[milestoneIndex]?.numberOfTasks;
}

function getProjectLeadNames(revision: Revision) {
  return getShortName(...(revision.projectLeads?.map((x) => x?.name) ?? []));
}

function CustomToolbar(_props: any) {
  return (
    <React.Fragment>
      <Portal container={() => document.getElementById('filter-panel')!}>
        <GridToolbarQuickFilter sx={{ width: '100%' }} />
      </Portal>
    </React.Fragment>
  );
}

function toDistictCategories(items: Revision[]): string[] {
  const set = new Set<string>();
  items.forEach((x) => {
    if (x.guide?.mainCategory?.title) {
      set.add(x.guide?.mainCategory.title);
    }
  });
  return Array.from(set).sort();
}
